'use client';

import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { useRouter, useSearchParams } from "next/navigation";
import { Item } from "../course-page/filters";
import { useMemo } from "react";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";

export const ChoosePlayers = ({
  players,
  setPlayers,
  playersOptions,
  availableSlots,
  isDisabled,
  className,
  teeTimeId,
  status,
  numberOfPlayers,
  id,
  supportsGroupBooking,
  allowSplit,
  groupBookingParams,
  isOfferCard,
  gaPage = "ttlist",
}: {
  players: string | number;
  setPlayers: (v: string) => void;
  playersOptions: string[];
  availableSlots: number;
  isDisabled?: boolean;
  className?: string;
  teeTimeId: string | undefined;
  status?: string;
  numberOfPlayers: string[];
  id?: string;
  supportsGroupBooking?: boolean;
  allowSplit?: boolean;
  groupBookingParams?: string;
  isOfferCard?: boolean;
  // GA page name for the players_<page> event (default ttlist).
  gaPage?: string;
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const urlWithCourse = useMemo(() => {
    const splitUrl = typeof window !== "undefined" ? window.location.href.split("/") : [];
    const courseUrl = splitUrl.slice(0, 4).join("/");
    return courseUrl;
  }, []);

  // Determine if second hand with no split is active
  const isSecondHandNoSplit = status === "SECOND_HAND" && !allowSplit;
  const isSelectionLocked = isSecondHandNoSplit || isOfferCard;
  const selectedPlayerStr = String(players);

  const handlePlayerChange = (value: string) => {
    if (isDisabled) return;
    if (availableSlots < parseInt(value)) return;

    // Enforce disabled options for second hand no split or offer card - only allow changing to fixed count
    if (isSelectionLocked && value !== selectedPlayerStr) return;

    if (!numberOfPlayers?.includes(value)) return;

    if (value) {
      setPlayers(value);
      googleAnalyticsEvent({
        action: `players_${gaPage}`,
        params: { item_id: teeTimeId, players: value, selected_value: value },
      });
      // Update URL with playerCount param if present
      const newSearchParams = new URLSearchParams(searchParams.toString());
      if (newSearchParams.has("playerCount")) {
        newSearchParams.set("playerCount", value);
        router.push(`${window.location.pathname}?${newSearchParams.toString()}`);
      }
    }
  };

  return (
    <ToggleGroup.Root
      type="single"
      value={selectedPlayerStr}
      onValueChange={handlePlayerChange}
      orientation="horizontal"
      className={`flex w-full ${className}`}
      data-testid="player-button-id"
      data-test={teeTimeId}
      data-qa={availableSlots}
      data-cy={players}
      id={id}
    >
      {playersOptions.map((value, index) => {
        // Disable all options except currently selected one if second hand no split or offer card
        const isOptionDisabled =
          isDisabled ||
          (availableSlots < index + 1) || // disable if slot not available
          (isSelectionLocked && value !== selectedPlayerStr) || // disable others if locked
          !numberOfPlayers?.includes(value); // disable if not allowed per numberOfPlayers

        return (
          <Item
            key={index}
            value={value}
            label={value}
            dataTestId="tee-time-player-id"
            dataTest={teeTimeId}
            dataQa={value}
            className={`flex-1 text-center flex items-center justify-center h-[33px] ${index === 0
              ? "rounded-l-full"
              : index === playersOptions.length - 1
                ? "rounded-r-full"
                : ""
              } border z-[1] border-stroke text-sm font-medium
                ${isOfferCard
                ? `data-[state=on]:!bg-[#5200be] data-[state=on]:text-white data-[state=off]:bg-white`
                : `data-[state=on]:bg-primary data-[state=on]:text-white`
              }
                ${isOptionDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          />
        );
      })}
      <div className={`ml-[0.25rem] flex items-center justify-center ${supportsGroupBooking ? "" : "hidden"}`}>
        {Boolean(supportsGroupBooking) && (
          <button
            className="rounded-full px-3 py-1 cursor-pointer border border-primary text-primary bg-white hover:bg-primary/5 transition-colors"
            onClick={() => {
              googleAnalyticsEvent({
                action: "btn_ttlist_group",
                params: { item_id: teeTimeId },
              });
              router.push(
                `${urlWithCourse}/group-booking?${groupBookingParams ?? ""}`
              );
            }}
            data-testid="group-booking-button-id"
          >
            5+
          </button>
        )}
      </div>
    </ToggleGroup.Root>
  );
};
